import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, Truck, Search as SearchIcon, Mail, FileText, Plus, RefreshCw,
  Trash2, Download, Edit3, LayoutDashboard, KeyRound, FilePlus, Calculator, BookOpen, DollarSign
} from "lucide-react";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND, SERVICE_TYPES, SHIPMENT_STATUSES } from "../lib/brand";
import { Modal, StatusBadge, fmtDate } from "../components/admin/shared";
import { QuotesTab } from "../components/admin/QuotesTab";
import { ReceiptsTab } from "../components/admin/ReceiptsTab";
import { PricingTab } from "../components/admin/PricingTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "shipments", label: "Shipments", icon: Package },
  { id: "quotes", label: "Quotes & Bookings", icon: Calculator },
  { id: "receipts", label: "Warehouse Receipts", icon: BookOpen },
  { id: "pickups", label: "Pickups", icon: Truck },
  { id: "sourcing", label: "Sourcing", icon: SearchIcon },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "forms", label: "Forms", icon: FileText },
  { id: "submissions", label: "Submissions", icon: FilePlus },
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "account", label: "Account", icon: KeyRound },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  const [stats, setStats] = useState({});
  const [shipments, setShipments] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [sourcing, setSourcing] = useState([]);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, sh, p, so, m, t, su] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/shipments"),
        api.get("/pickup-requests"),
        api.get("/sourcing-requests"),
        api.get("/contact-messages"),
        api.get("/form-templates"),
        api.get("/submitted-forms"),
      ]);
      setStats(s.data); setShipments(sh.data); setPickups(p.data);
      setSourcing(so.data); setMessages(m.data); setTemplates(t.data); setSubmissions(su.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="admin-dashboard">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-64 bg-[#0F172A] text-white lg:min-h-screen p-4">
          <div className="flex items-center gap-3 px-2 py-3 mb-4">
            <img src={BRAND.logo} alt="Amplartic" className="h-10 w-10 object-contain bg-white rounded-sm p-1" />
            <div>
              <div className="font-display font-extrabold tracking-tight text-sm">AMPLARTIC</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Admin</div>
            </div>
          </div>
          <nav className="space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                    tab === t.id ? "bg-[#FFD700] text-[#1E2A78]" : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </nav>
          <div className="mt-6 pt-6 border-t border-white/10 px-2 text-xs text-slate-400">
            <div>Signed in as</div>
            <div className="text-white font-mono truncate">{user?.email}</div>
            <button onClick={() => { logout(); nav("/"); }} className="mt-3 text-xs text-[#FFD700] hover:underline" data-testid="sidebar-logout">Sign out</button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 lg:p-10">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight text-[#0F172A]">
              {TABS.find((t) => t.id === tab)?.label}
            </h1>
            <button onClick={loadAll} className="px-3 py-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 inline-flex items-center gap-2 text-sm" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>

          {tab === "overview" && <Overview stats={stats} setTab={setTab} />}
          {tab === "shipments" && <ShipmentsTab shipments={shipments} reload={loadAll} />}
          {tab === "quotes" && <QuotesTab />}
          {tab === "receipts" && <ReceiptsTab shipments={shipments} reloadShipments={loadAll} />}
          {tab === "pricing" && <PricingTab />}
          {tab === "pickups" && <RequestsTab data={pickups} endpoint="pickup-requests" reload={loadAll} title="Pickup" renderRow={(r) => (
            <>
              <td className="px-4 py-3 text-sm">{r.full_name}<div className="text-xs text-slate-500">{r.email}</div></td>
              <td className="px-4 py-3 text-sm">{r.phone}</td>
              <td className="px-4 py-3 text-sm">{r.city}, {r.country} → {r.destination}</td>
              <td className="px-4 py-3 text-sm">{r.service_type}</td>
              <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{r.items_description}</td>
            </>
          )} headers={["Customer", "Phone", "Route", "Service", "Items"]} />}
          {tab === "sourcing" && <RequestsTab data={sourcing} endpoint="sourcing-requests" reload={loadAll} title="Sourcing" renderRow={(r) => (
            <>
              <td className="px-4 py-3 text-sm">{r.full_name}<div className="text-xs text-slate-500">{r.email}</div></td>
              <td className="px-4 py-3 text-sm">{r.phone}</td>
              <td className="px-4 py-3 text-sm">{r.item_name} <span className="text-xs text-slate-500">x{r.quantity}</span></td>
              <td className="px-4 py-3 text-sm">{r.budget_usd ? `$${r.budget_usd}` : "—"}</td>
              <td className="px-4 py-3 text-sm">{r.delivery_country}</td>
            </>
          )} headers={["Customer", "Phone", "Item", "Budget", "Destination"]} />}
          {tab === "messages" && <RequestsTab data={messages} endpoint="contact-messages" reload={loadAll} title="Message" renderRow={(r) => (
            <>
              <td className="px-4 py-3 text-sm">{r.full_name}<div className="text-xs text-slate-500">{r.email}</div></td>
              <td className="px-4 py-3 text-sm">{r.phone || "—"}</td>
              <td className="px-4 py-3 text-sm font-semibold">{r.subject}</td>
              <td className="px-4 py-3 text-xs text-slate-600 max-w-md truncate">{r.message}</td>
            </>
          )} headers={["From", "Phone", "Subject", "Message"]} />}
          {tab === "forms" && <FormsTab templates={templates} reload={loadAll} />}
          {tab === "submissions" && <SubmissionsTab submissions={submissions} reload={loadAll} />}
          {tab === "account" && <AccountTab />}
        </main>
      </div>
    </div>
  );
}

function Overview({ stats, setTab }) {
  const cards = [
    { k: "shipments", l: "Total Shipments", c: "bg-[#1E2A78]", tab: "shipments" },
    { k: "in_transit", l: "In Transit", c: "bg-[#FFD700] text-[#1E2A78]", tab: "shipments" },
    { k: "delivered", l: "Delivered", c: "bg-green-700", tab: "shipments" },
    { k: "pickup_requests", l: "New Pickups", c: "bg-[#0F172A]", tab: "pickups" },
    { k: "sourcing_requests", l: "New Sourcing", c: "bg-[#1E2A78]", tab: "sourcing" },
    { k: "contact_messages", l: "New Messages", c: "bg-[#0F172A]", tab: "messages" },
    { k: "submissions", l: "Form Submissions", c: "bg-[#1E2A78]", tab: "submissions" },
    { k: "quotes", l: "New Quotes & Bookings", c: "bg-[#FFD700] text-[#1E2A78]", tab: "quotes" },
    { k: "receipts", l: "Warehouse Receipts", c: "bg-[#0F172A]", tab: "receipts" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-grid">
      {cards.map((c) => (
        <button key={c.k} onClick={() => setTab(c.tab)} className={`${c.c} text-white p-6 rounded-sm text-left hover:opacity-90 transition-opacity`} data-testid={`stat-${c.k}`}>
          <div className="text-4xl font-display font-extrabold">{stats[c.k] ?? 0}</div>
          <div className="text-xs uppercase tracking-wider mt-2 opacity-90">{c.l}</div>
        </button>
      ))}
    </div>
  );
}

function ShipmentsTab({ shipments, reload }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    try { await api.delete(`/shipments/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2" data-testid="create-shipment-btn">
          <Plus className="h-4 w-4" /> New Shipment
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">Tracking #</th>
              <th className="px-4 py-3 text-left">Sender → Receiver</th>
              <th className="px-4 py-3 text-left">Route</th>
              <th className="px-4 py-3 text-left">Service</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shipments.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No shipments yet.</td></tr>
            )}
            {shipments.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`shipment-row-${s.tracking_number}`}>
                <td className="px-4 py-3 font-mono text-xs">{s.tracking_number}</td>
                <td className="px-4 py-3">{s.sender_name} <span className="text-slate-400">→</span> {s.receiver_name}</td>
                <td className="px-4 py-3">{s.origin} → {s.destination}</td>
                <td className="px-4 py-3">{s.service_type}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1E2A78]/10 text-[#1E2A78] text-[10px] uppercase tracking-wider font-semibold">{s.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(s.updated_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(s)} className="text-[#1E2A78] hover:underline mr-3 inline-flex items-center gap-1 text-xs" data-testid={`edit-${s.id}`}><Edit3 className="h-3.5 w-3.5" /> Update</button>
                  <button onClick={() => del(s.id)} className="text-red-600 hover:underline inline-flex items-center gap-1 text-xs" data-testid={`delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateShipmentModal onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); reload(); }} />}
      {editing && <UpdateShipmentModal shipment={editing} onClose={() => setEditing(null)} onSuccess={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function CreateShipmentModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    tracking_number: "",
    sender_name: "", sender_phone: "", receiver_name: "", receiver_phone: "",
    origin: "Mississauga, ON, Canada", destination: "Kingston, Jamaica",
    service_type: "Sea Freight", description: "", weight_kg: "",
    status: "Pending Pickup", estimated_delivery: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tracking_number: form.tracking_number.trim() ? form.tracking_number.trim().toUpperCase() : null,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      };
      const { data } = await api.post("/shipments", payload);
      toast.success(`Shipment ${data.tracking_number} created`);
      onSuccess();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 bg-white border border-slate-200 rounded-sm text-sm outline-none focus:border-[#1E2A78]";
  return (
    <Modal title="New Shipment" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="create-shipment-form">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Tracking # <span className="text-slate-400 normal-case font-normal">(optional — leave blank to auto-generate)</span>
          </label>
          <input
            placeholder="e.g. AMP2602ABC12345"
            className={`${inp} font-mono uppercase`}
            value={form.tracking_number}
            onChange={(e) => set("tracking_number", e.target.value)}
            data-testid="cs-tracking-number"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Sender name" className={inp} value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="cs-sender" />
          <input placeholder="Sender phone" className={inp} value={form.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} />
          <input required placeholder="Receiver name" className={inp} value={form.receiver_name} onChange={(e) => set("receiver_name", e.target.value)} data-testid="cs-receiver" />
          <input placeholder="Receiver phone" className={inp} value={form.receiver_phone} onChange={(e) => set("receiver_phone", e.target.value)} />
          <input required placeholder="Origin" className={inp} value={form.origin} onChange={(e) => set("origin", e.target.value)} />
          <input required placeholder="Destination" className={inp} value={form.destination} onChange={(e) => set("destination", e.target.value)} />
          <select className={inp} value={form.service_type} onChange={(e) => set("service_type", e.target.value)} data-testid="cs-service">
            {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select className={inp} value={form.status} onChange={(e) => set("status", e.target.value)}>
            {SHIPMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input placeholder="Weight (kg)" type="number" step="0.1" className={inp} value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} />
          <input placeholder="ETA (e.g., 2026-03-10)" className={inp} value={form.estimated_delivery} onChange={(e) => set("estimated_delivery", e.target.value)} />
        </div>
        <textarea placeholder="Description (barrels, boxes, items...)" rows="2" className={inp} value={form.description} onChange={(e) => set("description", e.target.value)} />
        <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] disabled:opacity-60" data-testid="cs-submit">
          {saving ? "Creating..." : "Create Shipment"}
        </button>
      </form>
    </Modal>
  );
}

function UpdateShipmentModal({ shipment, onClose, onSuccess }) {
  const [form, setForm] = useState({ status: shipment.status, location: "", note: "", estimated_delivery: shipment.estimated_delivery || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/shipments/${shipment.id}`, form);
      toast.success("Updated");
      onSuccess();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 bg-white border border-slate-200 rounded-sm text-sm outline-none focus:border-[#1E2A78]";
  return (
    <Modal title={`Update ${shipment.tracking_number}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="update-shipment-form">
        <select className={inp} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="us-status">
          {SHIPMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input placeholder="Location (e.g., Port of Kingston)" className={inp} value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="us-location" />
        <input placeholder="ETA" className={inp} value={form.estimated_delivery} onChange={(e) => set("estimated_delivery", e.target.value)} />
        <textarea placeholder="Note (optional)" rows="2" className={inp} value={form.note} onChange={(e) => set("note", e.target.value)} />
        <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] disabled:opacity-60" data-testid="us-submit">
          {saving ? "Saving..." : "Save Update"}
        </button>
      </form>
    </Modal>
  );
}

function RequestsTab({ data, endpoint, reload, title, renderRow, headers }) {
  const updateStatus = async (id, status) => {
    try { await api.patch(`/${endpoint}/${id}`, { status }); toast.success("Status updated"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid={`${endpoint}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <tr>
            {headers.map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Received</th>
            <th className="px-4 py-3 text-right">Update</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && <tr><td colSpan={headers.length + 3} className="px-4 py-10 text-center text-slate-400">No {title.toLowerCase()} requests yet.</td></tr>}
          {data.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              {renderRow(r)}
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
              <td className="px-4 py-3 text-right">
                <select onChange={(e) => updateStatus(r.id, e.target.value)} value={r.status} className="text-xs px-2 py-1 border border-slate-200 rounded-sm" data-testid={`status-${r.id}`}>
                  <option value="new">new</option>
                  <option value="in_progress">in_progress</option>
                  <option value="completed">completed</option>
                  <option value="rejected">rejected</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FormsTab({ templates, reload }) {
  const [showUpload, setShowUpload] = useState(false);
  const del = async (id) => {
    if (!window.confirm("Delete this form template?")) return;
    try { await api.delete(`/form-templates/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setShowUpload(true)} className="px-4 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2" data-testid="upload-template-btn">
          <Plus className="h-4 w-4" /> Upload Template
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Filename</th>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3 text-left">Uploaded</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No form templates yet.</td></tr>}
            {templates.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-display font-semibold">{t.title}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.filename}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{t.description}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`${API}/form-templates/${t.id}/download`} target="_blank" rel="noopener noreferrer" className="text-[#1E2A78] hover:underline mr-3 inline-flex items-center gap-1 text-xs"><Download className="h-3.5 w-3.5" /> Preview</a>
                  <button onClick={() => del(t.id)} className="text-red-600 hover:underline inline-flex items-center gap-1 text-xs"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showUpload && <UploadTemplateModal onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); reload(); }} />}
    </div>
  );
}

function UploadTemplateModal({ onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Attach a file"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", desc);
      fd.append("file", file);
      await api.post("/form-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded");
      onSuccess();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const inp = "w-full px-3 py-2 bg-white border border-slate-200 rounded-sm text-sm outline-none focus:border-[#1E2A78]";
  return (
    <Modal title="Upload Form Template" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="upload-template-form">
        <input required placeholder="Title (e.g. Cargo Pickup Form)" className={inp} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ut-title" />
        <textarea placeholder="Description" rows="2" className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} />
        <input required type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="ut-file" />
        <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] disabled:opacity-60" data-testid="ut-submit">
          {saving ? "Uploading..." : "Upload"}
        </button>
      </form>
    </Modal>
  );
}

function SubmissionsTab({ submissions, reload }) {
  const updateStatus = async (id, status) => {
    try { await api.patch(`/submitted-forms/${id}`, { status }); toast.success("Updated"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (id, filename) => {
    try {
      const r = await api.get(`/submitted-forms/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="submissions-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <tr>
            <th className="px-4 py-3 text-left">Customer</th>
            <th className="px-4 py-3 text-left">Form Type</th>
            <th className="px-4 py-3 text-left">File</th>
            <th className="px-4 py-3 text-left">Note</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Received</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No submissions yet.</td></tr>}
          {submissions.map((s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-4 py-3">{s.full_name}<div className="text-xs text-slate-500">{s.email}</div></td>
              <td className="px-4 py-3">{s.form_type}</td>
              <td className="px-4 py-3 font-mono text-xs">{s.filename}</td>
              <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{s.note}</td>
              <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(s.created_at)}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => download(s.id, s.filename)} className="text-[#1E2A78] hover:underline mr-3 inline-flex items-center gap-1 text-xs" data-testid={`sub-download-${s.id}`}><Download className="h-3.5 w-3.5" /> Download</button>
                <select onChange={(e) => updateStatus(s.id, e.target.value)} value={s.status} className="text-xs px-2 py-1 border border-slate-200 rounded-sm">
                  <option value="new">new</option>
                  <option value="in_progress">in_progress</option>
                  <option value="completed">completed</option>
                  <option value="rejected">rejected</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountTab() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", new_email: user?.email || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/update", form);
      toast.success("Credentials updated. Please sign in again.");
      if (form.new_email && form.new_email !== user.email) {
        setUser({ ...user, email: form.new_email });
      }
      setForm({ current_password: "", new_password: "", new_email: form.new_email });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const inp = "w-full px-3 py-2 bg-white border border-slate-200 rounded-sm text-sm outline-none focus:border-[#1E2A78]";
  return (
    <div className="max-w-md bg-white border border-slate-200 p-6 rounded-sm" data-testid="account-tab">
      <h2 className="text-lg font-display font-bold text-[#0F172A]">Update Admin Credentials</h2>
      <p className="text-xs text-slate-500 mt-1">Change your admin login email and/or password.</p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input required type="password" placeholder="Current password" className={inp} value={form.current_password} onChange={(e) => set("current_password", e.target.value)} data-testid="account-current" />
        <input type="email" placeholder="New email" className={inp} value={form.new_email} onChange={(e) => set("new_email", e.target.value)} data-testid="account-email" />
        <input required type="password" placeholder="New password (min 6 chars)" minLength="6" className={inp} value={form.new_password} onChange={(e) => set("new_password", e.target.value)} data-testid="account-new" />
        <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] disabled:opacity-60" data-testid="account-submit">
          {saving ? "Saving..." : "Update Credentials"}
        </button>
      </form>
    </div>
  );
}
